import React from 'react';
/**
 * BarChartRenderer — Interactive bar chart with selection.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s67.
 * Optimized: extracted shared data normalization + selection hook (~210r → ~110r).
 */
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell } from 'recharts';
import { QuestionRendererProps } from './types';
import { normalizeChartData, ChartDataPoint } from './chartDataUtils';
import { useChartSelection } from './useChartSelection';
import { FALLBACK_COLOR, hexToRgba, CHART_OPTION_BUTTON_CLASS } from './rendererUtils';
export function BarChartRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}: QuestionRendererProps) {
  const color = moduleColor || FALLBACK_COLOR;
  const config = question.data || {};
  const data = normalizeChartData(config);
  const xAxisLabel = config.xAxisLabel || '';
  const yAxisLabel = config.yAxisLabel || '';
  // Sprint R2 F10: derive from moduleColor, allow seed override via config.
  const barColor = config.barColor || hexToRgba(color, 0.75);
  const activeBarColor = config.activeBarColor || color;
  const {
    selectedIndex,
    handleItemClick,
    handleKeyDown
  } = useChartSelection({
    questionId: question.id,
    itemCount: data.length,
    idPrefix: `bar-option-${question.id}`,
    onAnswer,
    onReadyChange,
    shouldValidate
  });
  if (data.length === 0) {
    return <div className="text-center p-8 bg-red-50 rounded-xl border-2 border-red-200" data-id="element-11292" data-mp="3s57qp1totpm">
        <p className="text-red-700 font-bold" data-id="element-11293" data-mp="8qpylxv6q11e">
          ⚠️ Grafiek kan niet worden geladen
        </p>
        <p className="text-sm text-red-600 mt-2" data-id="element-11294" data-mp="djrfuciv6co5">Geen data beschikbaar.</p>
      </div>;
  }
  return <div className="w-full max-w-3xl mx-auto flex flex-col items-center" data-id="element-11295" data-mp="hirxhrplv99k">
      <div className="w-full h-[240px] md:h-[340px] bg-white rounded-xl border border-slate-200 shadow-sm p-3 md:p-4" aria-hidden="true" data-id="element-11296" data-mp="mjxq7pj9mwj3">
        <ResponsiveContainer width="100%" height="100%" data-id="element-11297" data-mp="rfwhbkvzasn7">
          <BarChart data={data} margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 40
        }} data-id="element-11298" data-mp="2nm5maoig1h0k">
            <CartesianGrid strokeDasharray="3 3" vertical={false} data-id="element-11299" data-mp="2xc8kr48c3n6a" />
            <XAxis dataKey="label" interval={0} tick={{
            fill: '#64748b',
            fontSize: 12
          }} tickLine={false} axisLine={{
            stroke: '#cbd5e1'
          }} label={xAxisLabel ? {
            value: xAxisLabel,
            position: 'insideBottom',
            offset: -20,
            fill: '#475569',
            fontSize: 13,
            fontWeight: 600
          } : undefined} data-id="element-11300" data-mp="2skqjsg6b9mlb" />
            <YAxis tick={{
            fill: '#64748b',
            fontSize: 12
          }} tickLine={false} axisLine={false} label={yAxisLabel ? {
            value: yAxisLabel,
            angle: -90,
            position: 'insideLeft',
            fill: '#475569',
            fontSize: 14,
            fontWeight: 600
          } : undefined} data-id="element-11301" data-mp="1gza3aisu3mas" />
            <Bar dataKey="value" onClick={handleItemClick} cursor="pointer" radius={[4, 4, 0, 0]} data-id="element-11302" data-mp="3eugtfpfpu55o">
              {data.map((_: ChartDataPoint, index: number) => <Cell key={`cell-${index}`} fill={selectedIndex === index ? activeBarColor : barColor} stroke={selectedIndex === index ? color : 'none'} strokeWidth={2} data-id="element-11303" data-mp="y48oqj5dw4oh" data-mpi={index} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div role="listbox" aria-label="Kies een staaf" className="flex flex-wrap justify-center gap-2 mt-4" data-id="element-11304" data-mp="2v67fv2sf3a58">
        {data.map((entry: ChartDataPoint, index: number) => <button key={`option-${index}`} id={`bar-option-${question.id}-${index}`} role="option" aria-selected={selectedIndex === index} tabIndex={0} onClick={() => handleItemClick(null, index)} onKeyDown={e => handleKeyDown(e, index)} className={CHART_OPTION_BUTTON_CLASS} style={selectedIndex === index ? {
        backgroundColor: color,
        color: '#ffffff',
        ['--tw-ring-color' as string]: hexToRgba(color, 0.5)
      } as React.CSSProperties : {
        backgroundColor: '#f1f5f9',
        color: '#334155',
        ['--tw-ring-color' as string]: hexToRgba(color, 0.5)
      } as React.CSSProperties} data-id="element-11305" data-mp="2l66ulbazmjye" data-mpi={index}>
            {entry.label}
          </button>)}
      </div>

      <p className="text-center text-slate-500 text-sm mt-4" data-id="element-11306" data-mp="1xbn3em062tjx">
        Klik op een staaf of gebruik de pijltjestoetsen
      </p>
    </div>;
}